import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { formatDate } from "../../data/format";
import { Badge, EmptyState, SkeletonText, ClockIcon } from "../../ui";

/**
 * Activity tab — the client-hub view of the Activity spine (ROLE-DESIGN
 * Part 5). Contract pre-agreed with the Coordinator (2026-07-11):
 * GET /api/activities/client/:clientId, bare array, newest first, scoped
 * server-side per tier. Behind FEATURES.activityFeed (default OFF) until
 * Session H deploys it; OFF shows a designed explanation, not a blank.
 */

const KIND_BADGES = {
  note: { label: "Note", variant: "neutral" },
  stage_change: { label: "Stage", variant: "info" },
  referral_created: { label: "Referral", variant: "brand" },
  quote_received: { label: "Quote", variant: "info" },
  quote_updated: { label: "Quote", variant: "info" },
  sr_created: { label: "Service", variant: "warning" },
  sr_updated: { label: "Service", variant: "warning" },
  doc_uploaded: { label: "Document", variant: "neutral" },
  client_converted: { label: "Converted", variant: "success" },
  sentinel_sync: { label: "Sentinel", variant: "neutral" },
  needs_review: { label: "Needs review", variant: "danger" },
  admin_action: { label: "Admin", variant: "neutral" }
};

function ActivityTab({ household }) {
  const [state, setState] = useState({ loading: FEATURES.activityFeed, items: [], failed: false });

  useEffect(() => {
    if (!FEATURES.activityFeed) {
      return undefined;
    }
    let cancelled = false;
    API.getActivitiesForClient(household.id)
      .then((res) => {
        if (!cancelled) {
          setState({
            loading: false,
            items: Array.isArray(res.data) ? res.data : [],
            failed: false
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ loading: false, items: [], failed: true });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [household]);

  if (!FEATURES.activityFeed) {
    return (
      <EmptyState
        icon={<ClockIcon size={32} />}
        title="Activity feed is on its way"
        message="Notes, stage changes, quotes, service updates, and document uploads will appear here as one timeline once the Activity backend ships (in progress)."
      />
    );
  }

  if (state.loading) {
    return <SkeletonText lines={5} />;
  }

  if (state.failed) {
    return (
      <EmptyState
        icon={<ClockIcon size={32} />}
        title="Activity couldn't be loaded"
        message="Something went wrong fetching this household's activity."
      />
    );
  }

  if (state.items.length === 0) {
    return (
      <EmptyState
        icon={<ClockIcon size={32} />}
        title="No activity yet"
        message="Notes and events about this household will collect here."
      />
    );
  }

  return (
    <div className="feat-feed" role="list">
      {state.items.map((item) => {
        const kind = KIND_BADGES[item.kind] || { label: item.kind, variant: "neutral" };
        return (
          <div
            key={item._id}
            className="feat-feed__row"
            style={{ cursor: "default" }}
            role="listitem"
          >
            <Badge variant={kind.variant}>{kind.label}</Badge>
            <span className="feat-feed__main">
              <span className="feat-feed__title" style={{ whiteSpace: "normal" }}>
                {item.body}
              </span>
              <span className="feat-feed__meta">
                {[formatDate(item.created_at), item.source !== "app" ? item.source : null]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default ActivityTab;
